/**
 * T6: Booking Matrix panel.
 *
 * Synthetic view of booking confirmations per country and per division.
 * Rows are grouped Division > Brand > Project, columns are dynamic Hubs.
 * Each cell shows a colored dot (green ≥100%, yellow 1-99%, grey 0)
 * + booked-assets / total-pre-forecast count.
 *
 * The pure helpers (computeBookingMatrix, classifyCellRate) are exported
 * for unit testing without the DOM.
 */

import { getState, subscribe } from "../state";
import { onPanelActivate, getActivePanel } from "../router";
import { unique, normalizeValue } from "./shared";
import { escapeHtml } from "../utils/escapeHtml";
import { showCellDrilldown, closeCellDrilldown } from "../components/CellDrilldown";
import { openValidationDrawer } from "../components/ValidationDrawer";
import { getProvider } from "../providerInstance";
import { showToast } from "../components/Toast";
import { triggerRefresh } from "../refreshService";

// ── Pure logic ──────────────────────────────────────────────

export interface BookingCell {
  preForecast: number;
  booked: number;
}

export interface BookingProjectRow {
  division: string;
  brand: string;
  project: string;
  cellsByHub: Map<string, BookingCell>;
}

export interface BookingMatrixData {
  hubs: string[];
  rows: BookingProjectRow[];
  totalPreForecast: number;
  totalBooked: number;
  bookingRate: number; // 0..1
}

export function classifyCellRate(cell: BookingCell): "empty" | "partial" | "full" {
  if (cell.preForecast === 0 && cell.booked === 0) return "empty";
  const rate = cell.preForecast === 0 ? 1 : cell.booked / cell.preForecast;
  if (rate >= 1) return "full";
  if (rate > 0) return "partial";
  return "empty";
}

interface ComputeInput {
  assets: any[];
  forecasts: any[];
  filter?: { division?: string; brand?: string; quarter?: string; month?: string };
}

export function computeBookingMatrix({ assets, forecasts, filter = {} }: ComputeInput): BookingMatrixData {
  // Numbers sourced from Forecasts tab, Asset_Forecast column (= forecast.quantity).
  // preForecast = sum of assetVolume (planned) from the Assets tab.
  // booked      = sum of quantity (Asset_Forecast) from the Forecasts tab.
  const bookedVolumeByAssetId = new Map<string, number>();
  for (const f of forecasts as any[]) {
    const id = String(f.assetId);
    bookedVolumeByAssetId.set(id, (bookedVolumeByAssetId.get(id) || 0) + (Number(f.quantity) || 0));
  }

  // Filter assets according to selection
  const visible = assets.filter((a: any) => {
    if (filter.division && a.division !== filter.division) return false;
    if (filter.brand && a.brand !== filter.brand) return false;
    if (filter.quarter && a.quarter !== filter.quarter) return false;
    if (filter.month) {
      // month is "YYYY-MM" or whatever the filter passes; do a substring match
      const date = String(a.requestedDeliveryDate || "");
      if (!date.includes(filter.month)) return false;
    }
    return true;
  });

  const hubs = unique(visible.map((a: any) => a.hub).filter(Boolean)).sort();

  // group: project key -> { division, brand, project, cellsByHub }
  const groups = new Map<string, BookingProjectRow>();

  for (const asset of visible) {
    const division = String(asset.division || "Unknown");
    const brand = String(asset.brand || "Unknown");
    const project = String(asset.projectName || asset.name || asset.id || "Unknown");
    const hub = String(asset.hub || "");
    if (!hub) continue;
    const key = `${division}::${brand}::${project}`;
    let row = groups.get(key);
    if (!row) {
      row = { division, brand, project, cellsByHub: new Map() };
      groups.set(key, row);
    }
    let cell = row.cellsByHub.get(hub);
    if (!cell) {
      cell = { preForecast: 0, booked: 0 };
      row.cellsByHub.set(hub, cell);
    }
    cell.preForecast += Number(asset.assetVolume) || 0;
    cell.booked += bookedVolumeByAssetId.get(String(asset.id)) || 0;
  }

  // Sort rows: division > brand > project
  const rows = [...groups.values()].sort((a, b) => {
    if (a.division !== b.division) return a.division.localeCompare(b.division);
    if (a.brand !== b.brand) return a.brand.localeCompare(b.brand);
    return a.project.localeCompare(b.project);
  });

  let totalPreForecast = 0;
  let totalBooked = 0;
  for (const row of rows) {
    for (const cell of row.cellsByHub.values()) {
      totalPreForecast += cell.preForecast;
      totalBooked += cell.booked;
    }
  }
  const bookingRate = totalPreForecast === 0 ? 0 : totalBooked / totalPreForecast;

  return { hubs, rows, totalPreForecast, totalBooked, bookingRate };
}

// ── Sort state ─────────────────────────────────────────────

let _bmSortByRate = false;
let _bmSortAsc = true;

// ── Helpers ────────────────────────────────────────────────

/** Compact formatter: 1 200 000 → "1.2M", 15 000 → "15K", 500 → "500" */
function fmtVol(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
  return String(Math.round(n));
}

function computeRowRate(row: BookingProjectRow): number {
  let totalPre = 0, totalBooked = 0;
  for (const cell of row.cellsByHub.values()) {
    totalPre += cell.preForecast;
    totalBooked += cell.booked;
  }
  return totalPre === 0 ? 0 : (totalBooked / totalPre) * 100;
}

// ── DOM wiring ──────────────────────────────────────────────

export function init(): void {
  onPanelActivate("booking-matrix", render);
  subscribe("assets", () => {
    if (getActivePanel() === "booking-matrix") render();
  });
  subscribe("forecasts", () => {
    if (getActivePanel() === "booking-matrix") render();
  });
  bindFilters();

  // Rate column sort + cell drill-down
  document.addEventListener("click", (e) => {
    const rateHeader = (e.target as HTMLElement).closest(".bm-rate-header");
    if (rateHeader) {
      if (_bmSortByRate) _bmSortAsc = !_bmSortAsc;
      else { _bmSortByRate = true; _bmSortAsc = true; }
      render();
      return;
    }

    // Cell drill-down
    const cell = (e.target as HTMLElement).closest<HTMLElement>("[data-bm-project][data-bm-hub]");
    if (cell) {
      const project = cell.dataset.bmProject || "";
      const hub = cell.dataset.bmHub || "";
      const state = getState();

      // Find assets for this project + hub
      const projectAssets = (state.assets as any[]).filter((a: any) =>
        (a.projectName || a.name) === project && a.hub === hub && normalizeValue(a.parentChild) !== "parent"
      );
      const forecastVolumeByAsset = new Map<string, number>();
      for (const f of state.forecasts as any[]) {
        const id = String(f.assetId);
        forecastVolumeByAsset.set(id, (forecastVolumeByAsset.get(id) || 0) + (Number(f.quantity) || 0));
      }

      const drilldownAssets = projectAssets.map((a: any) => ({
        id: a.id,
        name: a.productName || a.name || a.id,
        ean: a.globalEAN || "",
        booked: forecastVolumeByAsset.has(a.id),
      }));

      const cellData = {
        preForecast: projectAssets.reduce((s, a: any) => s + (Number(a.assetVolume) || 0), 0),
        booked: projectAssets.reduce((s, a: any) => s + (forecastVolumeByAsset.get(a.id) || 0), 0),
      };

      showCellDrilldown({
        project,
        hub,
        booked: cellData.booked,
        preForecast: cellData.preForecast,
        assets: drilldownAssets,
        anchorEl: cell,
        onValidateAsset: (assetId) => {
          closeCellDrilldown();
          const asset = projectAssets.find((a: any) => a.id === assetId);
          if (!asset) return;
          openValidationDrawer({
            projectName: project,
            brand: asset.brand || "",
            division: asset.division || "",
            type: asset.type || "",
            parentDeliveryDate: asset.requestedDeliveryDate || "",
            assetCount: 1,
            totalVolume: Number(asset.assetVolume || 0),
            countries: [
              {
                code: hub,
                volume: Number(asset.assetVolume || 0),
                defaultDate: asset.requestedDeliveryDate || "",
                childIds: [String(assetId)],
              },
            ],
            onValidate: async ({ entries, owner, notes }) => {
              for (const entry of entries) {
                for (const childId of entry.childIds) {
                  await getProvider().upsertForecast({
                    assetId: childId,
                    date: entry.date,
                    quantity: entry.volume,
                    owner,
                    notes,
                    country: entry.country,
                  } as any);
                }
              }
              showToast("Asset validated.", "success");
              await triggerRefresh();
            },
            onClose: () => {},
          });
        },
      });
    }
  });
}

function bindFilters(): void {
  for (const id of [
    "bm-filter-division",
    "bm-filter-brand",
    "bm-filter-quarter",
    "bm-filter-month",
  ]) {
    document.getElementById(id)?.addEventListener("change", render);
  }
}

function fillFilter(id: string, values: string[]): void {
  const el = document.getElementById(id) as HTMLSelectElement | null;
  if (!el) return;
  const previous = el.value;
  el.innerHTML = `<option value="">All</option>` +
    values.map((v) => `<option value="${escapeHtml(v)}">${escapeHtml(v)}</option>`).join("");
  if (values.includes(previous)) el.value = previous;
}

export function render(): void {
  const state = getState();
  // Refill filters from current data
  fillFilter("bm-filter-division", unique(state.assets.map((a: any) => a.division).filter(Boolean)));
  fillFilter("bm-filter-brand", unique(state.assets.map((a: any) => a.brand).filter(Boolean)));
  fillFilter(
    "bm-filter-quarter",
    unique(state.assets.map((a: any) => a.quarter).filter(Boolean)),
  );
  fillFilter(
    "bm-filter-month",
    unique(
      state.assets
        .map((a: any) => {
          const d = String(a.requestedDeliveryDate || "");
          const m = d.match(/^(\d{4})-(\d{2})/);
          return m ? `${m[1]}-${m[2]}` : "";
        })
        .filter(Boolean),
    ).sort(),
  );

  const filter = {
    division: (document.getElementById("bm-filter-division") as HTMLSelectElement | null)?.value || "",
    brand: (document.getElementById("bm-filter-brand") as HTMLSelectElement | null)?.value || "",
    quarter: (document.getElementById("bm-filter-quarter") as HTMLSelectElement | null)?.value || "",
    month: (document.getElementById("bm-filter-month") as HTMLSelectElement | null)?.value || "",
  };

  // Count child assets per hub — each child represents one delivery slot for
  // its country. Assets with no parentChild value (e.g. legacy data) are also
  // included so they are not silently dropped. Parent assets are excluded to
  // avoid double-counting: a parent's hub duplicates its first child's hub and
  // forecasts are created against child IDs, not parent IDs.
  const children = state.assets.filter(
    (a: any) => normalizeValue(a.parentChild) !== "parent",
  );

  const data = computeBookingMatrix({
    assets: children,
    forecasts: state.forecasts as any,
    filter,
  });

  renderKpis(data);
  renderTable(data);
}

function renderKpis(data: BookingMatrixData): void {
  const set = (id: string, value: string): void => {
    const el = document.getElementById(id);
    if (el) el.textContent = value;
  };
  set("bm-kpi-pre", fmtVol(data.totalPreForecast));
  set("bm-kpi-booked", fmtVol(data.totalBooked));
  set("bm-kpi-rate", `${Math.round(data.bookingRate * 100)}%`);

  // Overdue: projects with 0 booking rate and past delivery date
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const state = getState();
  const overdue = data.rows.filter((row) => {
    const totalBooked = [...row.cellsByHub.values()].reduce((s, c) => s + c.booked, 0);
    const totalPre = [...row.cellsByHub.values()].reduce((s, c) => s + c.preForecast, 0);
    if (totalPre === 0 || totalBooked > 0) return false;
    // Find delivery date for this project
    const asset = state.assets.find((a: any) => (a.projectName || a.name) === row.project);
    if (!asset) return false;
    const dd = new Date(asset.requestedDeliveryDate || asset.deliveryDate || "");
    return !isNaN(dd.getTime()) && dd < today;
  }).length;
  set("bm-kpi-overdue", String(overdue));
}

function renderTable(data: BookingMatrixData): void {
  const head = document.getElementById("bm-table-head");
  const body = document.getElementById("bm-table-body");
  if (!head || !body) return;

  head.innerHTML =
    `<th>Division</th><th>Brand</th><th>Project</th>` +
    data.hubs.map((h) => `<th>${escapeHtml(h)}</th>`).join("") +
    `<th class="bm-rate-header" style="cursor:pointer" title="Click to sort by booking rate">Rate ${_bmSortByRate ? (_bmSortAsc ? "↑" : "↓") : "↕"}</th>`;

  if (!data.rows.length) {
    body.innerHTML = `<tr><td colspan="${4 + data.hubs.length}" style="color:var(--muted)">No projects match the current filters.</td></tr>`;
    return;
  }

  // Sort rows if needed
  const sortedRows = [...data.rows];
  if (_bmSortByRate) {
    sortedRows.sort((a, b) => {
      const rateA = computeRowRate(a);
      const rateB = computeRowRate(b);
      return _bmSortAsc ? rateA - rateB : rateB - rateA;
    });
  }

  body.innerHTML = sortedRows
    .map((row) => {
      const cells = data.hubs
        .map((hub) => {
          const cell = row.cellsByHub.get(hub) || { preForecast: 0, booked: 0 };
          const cls = classifyCellRate(cell);
          const label = cell.preForecast === 0
            ? (cell.booked > 0 ? `${fmtVol(cell.booked)}/—` : "—")
            : `${fmtVol(cell.booked)}/${fmtVol(cell.preForecast)}`;
          const isClickable = cell.preForecast > 0 || cell.booked > 0;
          const cellClass = `bm-cell bm-cell-${cls}${isClickable ? " bm-cell-clickable" : ""}`;
          const tooltip = (cell.preForecast === 0 && cell.booked === 0) ? "" : `title="${cell.booked.toLocaleString()} forecasted / ${cell.preForecast.toLocaleString()} planned volume (${Math.round((cell.preForecast === 0 ? 0 : cell.booked / cell.preForecast) * 100)}%) — Click to see details"`;
          const dataAttrs = isClickable ? `data-bm-project="${escapeHtml(row.project)}" data-bm-hub="${escapeHtml(hub)}"` : "";
          return `<td class="${cellClass}" ${tooltip} ${dataAttrs}>
            <span class="bm-dot bm-dot-${cls}" aria-hidden="true"></span>${label}
          </td>`;
        })
        .join("");
      const rowRate = computeRowRate(row);
      const rateColor = rowRate >= 100 ? "#16a34a" : rowRate > 0 ? "#facc15" : "#ef4444";
      return `<tr>
        <td>${escapeHtml(row.division)}</td>
        <td>${escapeHtml(row.brand)}</td>
        <td>${escapeHtml(row.project)}</td>
        ${cells}
        <td style="text-align:center;color:${rateColor};font-weight:700">${Math.round(rowRate)}%</td>
      </tr>`;
    })
    .join("");
}
